import { CaseCreationConfig } from "./CreatePracticeAreaCaseModal";

/**
 * Centralized caseConfig for every practice area.
 * Used by both the individual page components and the Matters page create flow.
 */
export const PRACTICE_AREA_CONFIGS: Record<string, CaseCreationConfig> = {
  criminal: {
    practiceAreaKey: "criminal",
    apiBasePath: "criminal",
    caseTypes: [
      { value: "dui", label: "DUI / Impaired Driving" },
      { value: "assault", label: "Assault" },
      { value: "domestic_violence", label: "Domestic Violence" },
      { value: "fraud", label: "Fraud" },
      { value: "drug_offences", label: "Drug Offences" },
      { value: "theft", label: "Theft" },
      { value: "sexual_offences", label: "Sexual Offences" },
      { value: "weapons", label: "Weapons" },
      { value: "young_offenders", label: "Young Offenders" },
    ],
  },
  personal_injury: {
    practiceAreaKey: "personal_injury",
    apiBasePath: "personal-injury",
    caseTypes: [
      { value: "mva", label: "Motor Vehicle Accident" },
      { value: "slip_fall", label: "Slip & Fall" },
      { value: "medical_malpractice", label: "Medical Malpractice" },
      { value: "workplace", label: "Workplace Injury" },
      { value: "product_liability", label: "Product Liability" },
      { value: "dog_bite", label: "Dog Bite" },
    ],
  },
  family: {
    practiceAreaKey: "family",
    apiBasePath: "family",
    caseTypes: [
      { value: "divorce", label: "Divorce" },
      { value: "separation", label: "Separation" },
      { value: "custody", label: "Custody" },
      { value: "support", label: "Support" },
      { value: "adoption", label: "Adoption" },
      { value: "cps", label: "Child Protection" },
      { value: "prenuptial", label: "Prenuptial" },
      { value: "property_division", label: "Property Division" },
      { value: "domestic_violence", label: "Domestic Violence" },
      { value: "other", label: "Other" },
    ],
  },
  litigation: {
    practiceAreaKey: "litigation",
    apiBasePath: "litigation",
    caseTypes: [
      { value: "breach_of_contract", label: "Breach of Contract" },
      { value: "negligence", label: "Negligence" },
      { value: "collections", label: "Collections" },
      { value: "employment_dispute", label: "Employment Dispute" },
      { value: "construction_lien", label: "Construction Lien" },
      { value: "insurance_dispute", label: "Insurance Dispute" },
      { value: "commercial", label: "Commercial" },
      { value: "judicial_review", label: "Judicial Review" },
      { value: "injunction", label: "Injunction" },
    ],
  },
  wills_estates: {
    practiceAreaKey: "wills_estates",
    apiBasePath: "estates",
    caseTypes: [
      { value: "will_drafting", label: "Will Drafting" },
      { value: "estate_administration", label: "Estate Administration" },
      { value: "probate", label: "Probate" },
      { value: "power_of_attorney", label: "Power of Attorney" },
      { value: "trust", label: "Trust" },
      { value: "estate_litigation", label: "Estate Litigation" },
      { value: "capacity", label: "Capacity" },
      { value: "guardianship", label: "Guardianship" },
      { value: "estate_planning", label: "Estate Planning" },
    ],
  },
  class_action: {
    practiceAreaKey: "class_action",
    apiBasePath: "class-action",
    caseTypes: [
      { value: "product_liability", label: "Product Liability" },
      { value: "securities", label: "Securities" },
      { value: "employment", label: "Employment" },
      { value: "consumer", label: "Consumer" },
      { value: "environmental", label: "Environmental" },
      { value: "privacy", label: "Privacy" },
      { value: "antitrust", label: "Antitrust" },
      { value: "government", label: "Government" },
      { value: "other", label: "Other" },
    ],
  },
  corporate: {
    practiceAreaKey: "corporate",
    apiBasePath: "corporate",
    caseTypes: [
      { value: "incorporation", label: "Incorporation" },
      { value: "amalgamation", label: "Amalgamation" },
      { value: "share_purchase", label: "Share Purchase" },
      { value: "asset_purchase", label: "Asset Purchase" },
      { value: "shareholder_agreement", label: "Shareholder Agreement" },
      { value: "articles_amendment", label: "Articles Amendment" },
      { value: "annual_compliance", label: "Annual Compliance" },
      { value: "joint_venture", label: "Joint Venture" },
      { value: "franchise", label: "Franchise" },
      { value: "reorganization", label: "Reorganization" },
    ],
  },
  employment_labour: {
    practiceAreaKey: "employment_labour",
    apiBasePath: "employment",
    caseTypes: [
      { value: "wrongful_dismissal", label: "Wrongful Dismissal" },
      { value: "constructive_dismissal", label: "Constructive Dismissal" },
      { value: "human_rights", label: "Human Rights" },
      { value: "workplace_safety", label: "Workplace Safety" },
      { value: "union_grievance", label: "Union Grievance" },
      { value: "employment_contract", label: "Employment Contract" },
      { value: "non_compete", label: "Non-Compete" },
      { value: "workplace_harassment", label: "Workplace Harassment" },
      { value: "accommodation", label: "Accommodation" },
      { value: "severance_negotiation", label: "Severance Negotiation" },
    ],
  },
  intellectual_property: {
    practiceAreaKey: "intellectual_property",
    apiBasePath: "ip",
    caseTypes: [
      { value: "patent_prosecution", label: "Patent Prosecution" },
      { value: "trademark_registration", label: "Trademark Registration" },
      { value: "copyright_registration", label: "Copyright Registration" },
      { value: "trade_secret", label: "Trade Secret" },
      { value: "licensing", label: "Licensing" },
      { value: "ip_litigation", label: "IP Litigation" },
      { value: "patent_infringement", label: "Patent Infringement" },
      { value: "trademark_opposition", label: "Trademark Opposition" },
      { value: "domain_dispute", label: "Domain Dispute" },
      { value: "ip_portfolio", label: "IP Portfolio" },
    ],
  },
  tax: {
    practiceAreaKey: "tax",
    apiBasePath: "tax",
    caseTypes: [
      { value: "tax_planning", label: "Tax Planning" },
      { value: "cra_audit", label: "CRA Audit" },
      { value: "notice_of_objection", label: "Notice of Objection" },
      { value: "tax_court_appeal", label: "Tax Court Appeal" },
      { value: "voluntary_disclosure", label: "Voluntary Disclosure" },
      { value: "corporate_restructuring", label: "Corporate Restructuring" },
      { value: "transfer_pricing", label: "Transfer Pricing" },
      { value: "gst_hst_dispute", label: "GST/HST Dispute" },
      { value: "international_tax", label: "International Tax" },
      { value: "estate_tax", label: "Estate Tax" },
    ],
  },
  environmental: {
    practiceAreaKey: "environmental",
    apiBasePath: "environmental",
    caseTypes: [
      { value: "environmental_assessment", label: "Environmental Assessment" },
      { value: "contaminated_site", label: "Contaminated Site" },
      { value: "permit_application", label: "Permit Application" },
      { value: "regulatory_compliance", label: "Regulatory Compliance" },
      { value: "enforcement_defence", label: "Enforcement Defence" },
      { value: "environmental_litigation", label: "Environmental Litigation" },
      { value: "species_at_risk", label: "Species at Risk" },
      { value: "water_rights", label: "Water Rights" },
      { value: "emissions", label: "Emissions" },
      { value: "waste_management", label: "Waste Management" },
    ],
  },
  bankruptcy_insolvency: {
    practiceAreaKey: "bankruptcy_insolvency",
    apiBasePath: "bankruptcy",
    caseTypes: [
      { value: "consumer_proposal", label: "Consumer Proposal" },
      { value: "personal_bankruptcy", label: "Personal Bankruptcy" },
      { value: "corporate_bankruptcy", label: "Corporate Bankruptcy" },
      { value: "receivership", label: "Receivership" },
      { value: "ccaa_proceeding", label: "CCAA Proceeding" },
      { value: "creditor_representation", label: "Creditor Representation" },
      { value: "preference_claim", label: "Preference Claim" },
      { value: "debt_restructuring", label: "Debt Restructuring" },
      { value: "cross_border_insolvency", label: "Cross-Border Insolvency" },
      { value: "directors_liability", label: "Directors Liability" },
    ],
  },
  administrative: {
    practiceAreaKey: "administrative",
    apiBasePath: "admin-law",
    caseTypes: [
      { value: "tribunal_hearing", label: "Tribunal Hearing" },
      { value: "judicial_review", label: "Judicial Review" },
      { value: "regulatory_appeal", label: "Regulatory Appeal" },
      { value: "licensing", label: "Licensing" },
      { value: "professional_discipline", label: "Professional Discipline" },
      { value: "freedom_of_information", label: "Freedom of Information" },
      { value: "municipal_law", label: "Municipal Law" },
      { value: "pension_appeal", label: "Pension Appeal" },
      { value: "immigration_appeal", label: "Immigration Appeal" },
      { value: "human_rights_tribunal", label: "Human Rights Tribunal" },
    ],
  },
  construction: {
    practiceAreaKey: "construction",
    apiBasePath: "construction",
    caseTypes: [
      { value: "construction_lien", label: "Construction Lien" },
      { value: "bond_claim", label: "Bond Claim" },
      { value: "delay_claim", label: "Delay Claim" },
      { value: "deficiency_claim", label: "Deficiency Claim" },
      { value: "contract_review", label: "Contract Review" },
      { value: "bid_dispute", label: "Bid Dispute" },
      { value: "prompt_payment", label: "Prompt Payment" },
      { value: "construction_arbitration", label: "Construction Arbitration" },
      { value: "occupational_health", label: "Occupational Health" },
      { value: "builders_lien", label: "Builders Lien" },
    ],
  },
  insurance: {
    practiceAreaKey: "insurance",
    apiBasePath: "insurance",
    caseTypes: [
      { value: "coverage_dispute", label: "Coverage Dispute" },
      { value: "subrogation", label: "Subrogation" },
      { value: "bad_faith", label: "Bad Faith" },
      { value: "policy_interpretation", label: "Policy Interpretation" },
      { value: "personal_injury_defence", label: "Personal Injury Defence" },
      { value: "property_damage", label: "Property Damage" },
      { value: "professional_liability", label: "Professional Liability" },
      { value: "directors_officers", label: "Directors & Officers" },
      { value: "cyber_insurance", label: "Cyber Insurance" },
      { value: "life_disability", label: "Life & Disability" },
    ],
  },
  entertainment: {
    practiceAreaKey: "entertainment",
    apiBasePath: "entertainment",
    caseTypes: [
      { value: "talent_agreement", label: "Talent Agreement" },
      { value: "production_contract", label: "Production Contract" },
      { value: "distribution_deal", label: "Distribution Deal" },
      { value: "content_licensing", label: "Content Licensing" },
      { value: "music_contract", label: "Music Contract" },
      { value: "publishing_deal", label: "Publishing Deal" },
      { value: "endorsement_deal", label: "Endorsement Deal" },
      { value: "rights_acquisition", label: "Rights Acquisition" },
      { value: "content_dispute", label: "Content Dispute" },
      { value: "guild_compliance", label: "Guild Compliance" },
    ],
  },
  technology: {
    practiceAreaKey: "technology",
    apiBasePath: "technology",
    caseTypes: [
      { value: "saas_agreement", label: "SaaS Agreement" },
      { value: "data_privacy", label: "Data Privacy" },
      { value: "cybersecurity_incident", label: "Cybersecurity Incident" },
      { value: "software_licensing", label: "Software Licensing" },
      { value: "tech_acquisition", label: "Tech Acquisition" },
      { value: "ai_governance", label: "AI Governance" },
      { value: "open_source", label: "Open Source" },
      { value: "cloud_services", label: "Cloud Services" },
      { value: "fintech_compliance", label: "Fintech Compliance" },
      { value: "data_breach_response", label: "Data Breach Response" },
    ],
  },
  real_estate: {
    practiceAreaKey: "real_estate",
    apiBasePath: "real-estate",
    backendCreatesMatter: true,
    caseTypes: [
      { value: "purchase", label: "Purchase" },
      { value: "sale", label: "Sale" },
      { value: "mortgage", label: "Mortgage" },
      { value: "refinance", label: "Refinance" },
      { value: "private_lending", label: "Private Lending" },
    ],
  },
};

/**
 * Mapping from practice area key to the title shown in the modal header.
 */
export const PRACTICE_AREA_TITLES: Record<string, string> = {
  criminal: "Criminal Defence",
  personal_injury: "Personal Injury",
  family: "Family Law",
  litigation: "Civil Litigation",
  wills_estates: "Wills, Estates & Trusts",
  class_action: "Class Action",
  corporate: "Corporate & Commercial",
  employment_labour: "Employment & Labour",
  intellectual_property: "Intellectual Property",
  tax: "Tax Law",
  environmental: "Environmental Law",
  bankruptcy_insolvency: "Bankruptcy & Insolvency",
  administrative: "Administrative Law",
  construction: "Construction Law",
  insurance: "Insurance Law",
  entertainment: "Entertainment & Media",
  technology: "Technology & Cyber",
  real_estate: "Real Estate",
};
